import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchUsageSummary, UsageSummary } from "../../../../services/usageService";
import { useFeatureAccess } from "../../../../features/feature-access";
import { AGENT_TYPE } from "../../../../constants/agentTypes";
import { AuditCheckupToggle } from "./AuditCheckupToggle";

type AgentOption = { id: string; name: string; avatar?: string; agentType?: string };

type NewChatHeaderProps = {
  agentName: string;
  agentType?: string;
  agentAvatar?: string;
  agentId?: string;
  customerId?: string;
  idToken?: string;
  availableAgents: AgentOption[];
  showAgentDropdown: boolean;
  embedded: boolean;
  headerBar: string;
  healthError: string | null;
  isChatHealthy: boolean | null;
  /** When true→false transition is detected, re-fetch usage to reflect just-completed AI response. */
  isLoading?: boolean;
  onAgentChange?: (agentId: string) => void;
  onEditAgent: () => void;
  onBack?: () => void;
};

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
}

function formatVideoTime(seconds: number): string {
  if (seconds <= 0) return "0s";
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  if (seconds >= 60) return `${Math.floor(seconds / 60)}m`;
  return `${Math.round(seconds)}s`;
}

function formatPeriod(period: string): string {
  const [year, month] = period.split("-");
  return new Date(Number(year), Number(month) - 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

const METRIC_CONFIGS = [
  {
    key: "tokens",
    label: "Tokens",
    icon: "bolt",
    color: "text-[#648b7f]",
    bgColor: "bg-[#648b7f]/10",
  },
  {
    key: "images",
    label: "Images",
    icon: "image",
    color: "text-[#e4b372]",
    bgColor: "bg-[#e4b372]/10",
  },
  {
    key: "video",
    label: "Video",
    icon: "videocam",
    color: "text-[#2f4941]",
    bgColor: "bg-[#2f4941]/10",
  },
  {
    key: "searches",
    label: "Deep Searches",
    icon: "travel_explore",
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
  },
] as const;

export const NewChatHeader: React.FC<NewChatHeaderProps> = ({
  agentName,
  agentType,
  agentAvatar,
  agentId,
  customerId,
  idToken,
  availableAgents,
  showAgentDropdown,
  embedded,
  headerBar,
  healthError,
  isChatHealthy,
  isLoading,
  onAgentChange,
  onEditAgent,
  onBack,
}) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [isUsageOpen, setIsUsageOpen] = useState(false);
  const usageCardRef = useRef<HTMLDivElement>(null);
  const prevLoadingRef = useRef(false);

  useEffect(() => {
    if (!customerId || !idToken) return;
    fetchUsageSummary(customerId, idToken)
      .then(setUsage)
      .catch(() => {});
  }, [customerId, idToken]);

  // Re-fetch usage when a streaming response completes (isLoading: true → false)
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      if (customerId && idToken) {
        fetchUsageSummary(customerId, idToken).then(setUsage).catch(() => {});
      }
    }
    prevLoadingRef.current = isLoading ?? false;
  }, [isLoading, customerId, idToken]);

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!isUsageOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (usageCardRef.current && !usageCardRef.current.contains(e.target as Node)) {
        setIsUsageOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isUsageOpen]);

  // Starter Brands see the AI Visibility Manager labelled Pro (SGL-449).
  const { canAccessFeature } = useFeatureAccess();
  const proLocked = (agentType?: string) =>
    agentType === AGENT_TYPE.VISIBILITY_MANAGER && !canAccessFeature("visibility_manager");
  const selectedAgent = availableAgents.find((a) => a.id === agentId) || availableAgents[0];

  const metricValues: Record<string, string> = usage
    ? {
        tokens: formatTokens(usage.tokens?.total_tokens_total ?? 0),
        images: String(usage.images ?? 0),
        video: formatVideoTime(usage.video_seconds ?? 0),
        searches: String(usage.tokens?.perplexity_queries_total ?? 0),
      }
    : {};

  return (
    <header
      className={`relative z-[50] flex flex-shrink-0 items-center justify-between ${headerBar} ${
        embedded ? "min-h-[44px] px-3 py-1.5" : "px-4 py-2.5"
      }`}
    >
      {/* Left: agent avatar + dropdown */}
      <div className="flex min-w-0 items-center gap-2.5">
        {agentAvatar ? (
          <img
            alt={agentName || "Agent Avatar"}
            className="h-9 w-9 shrink-0 rounded-full border border-theme-border object-cover shadow-sm"
            src={agentAvatar}
          />
        ) : (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-theme-border bg-theme-surface text-[14px] font-bold text-theme-fg shadow-sm">
            {agentName ? agentName.charAt(0).toUpperCase() : "A"}
          </div>
        )}
        {showAgentDropdown ? (
          <div className="relative z-20 flex min-w-0 items-center gap-2" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex max-w-[min(100%,16rem)] items-center justify-between gap-2 truncate rounded-lg border border-theme-border bg-theme-surface py-1 pl-2.5 pr-2 text-[14px] font-semibold leading-snug text-theme-fg shadow-sm transition-all hover:bg-theme-secondary/10 focus:border-theme-primary focus:outline-none focus:ring-1 focus:ring-theme-primary/30"
            >
              <span className="truncate">{selectedAgent?.name || "Select Agent"}</span>
              <span
                className={`material-icons text-[20px] text-theme-fg-muted transition-transform ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
              >
                expand_more
              </span>
            </button>

            {isDropdownOpen && (
              <div className="absolute left-0 top-full z-[100] mt-1.5 max-h-60 w-max min-w-full overflow-auto rounded-lg border border-theme-border bg-theme-surface py-1.5 shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none custom-scrollbar">
                {availableAgents.map((agent) => (
                  <button
                    key={agent.id}
                    onClick={() => {
                      onAgentChange?.(agent.id);
                      setIsDropdownOpen(false);
                    }}
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-all duration-200 hover:scale-[1.02] hover:z-10 ${
                      agent.id === agentId
                        ? "bg-brand-primary/10 text-brand-primary"
                        : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-theme-fg"
                    }`}
                  >
                    <span className="truncate text-sm font-medium text-theme-fg">{agent.name}</span>
                    {proLocked(agent.agentType) && (
                      <span className="ml-auto rounded-full bg-brand-primary/10 px-2 py-0.5 text-[11px] font-semibold text-brand-primary">
                        Pro
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {isChatHealthy === true && (
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-theme-primary shadow-sm"
                title="Chat service is online"
              />
            )}
          </div>
        ) : (
          <h2 className="flex min-w-0 items-center gap-2 text-[15px] font-semibold leading-snug text-theme-fg">
            <span className="truncate">{agentName}</span>
            {isChatHealthy === true && (
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-theme-primary shadow-sm"
                title="Chat service is online"
              />
            )}
          </h2>
        )}
        {healthError && (
          <div className="ml-1 rounded border border-red-100 bg-red-50 px-1.5 py-0.5 text-[11px] font-medium leading-tight text-red-600 shadow-sm">
            {healthError}
          </div>
        )}
      </div>

      {/* Right: usage button + edit/back */}
      <div className="flex shrink-0 items-center gap-2">
        {agentType === AGENT_TYPE.VISIBILITY_MANAGER && customerId && idToken && !proLocked(agentType) && (
          <AuditCheckupToggle customerId={customerId} idToken={idToken} />
        )}
        {usage && (
          <div className="relative" ref={usageCardRef}>
            <button
              type="button"
              onClick={() => setIsUsageOpen((prev) => !prev)}
              title="View usage"
              className={`flex items-center justify-center rounded-lg border border-theme-border bg-theme-surface p-1.5 shadow-sm transition-colors hover:bg-theme-background ${
                isUsageOpen ? "bg-theme-background ring-1 ring-theme-primary/30" : ""
              }`}
            >
              <span className="material-icons-outlined text-[18px] text-theme-fg-muted">bar_chart</span>
            </button>

            {isUsageOpen && (
              <div className="absolute right-0 top-[calc(100%+8px)] z-[200] w-[260px] overflow-hidden rounded-xl border border-theme-border bg-theme-surface shadow-2xl ring-1 ring-black/5">
                {/* Card header */}
                <div className="flex items-center justify-between border-b border-theme-border bg-theme-background px-4 py-3">
                  <div>
                    <p className="text-[13px] font-semibold text-theme-fg">Usage Summary</p>
                    <p className="text-[11px] text-theme-fg-muted">{formatPeriod(usage.period)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsUsageOpen(false)}
                    className="flex items-center justify-center rounded-md p-0.5 text-theme-fg-muted transition-colors hover:bg-theme-border hover:text-theme-fg"
                  >
                    <span className="material-icons text-[16px]">close</span>
                  </button>
                </div>

                {/* 2×2 metric grid */}
                <div className="grid grid-cols-2 gap-2 p-3">
                  {METRIC_CONFIGS.map(({ key, label, icon, color, bgColor }) => (
                    <div
                      key={key}
                      className="flex flex-col items-center gap-1.5 rounded-lg border border-theme-border bg-theme-background px-3 py-3"
                    >
                      <div className={`flex items-center justify-center rounded-full ${bgColor} p-1.5`}>
                        <span className={`material-icons-outlined text-[18px] ${color}`}>{icon}</span>
                      </div>
                      <span className="text-[22px] font-bold leading-none text-theme-fg">
                        {metricValues[key]}
                      </span>
                      <span className="text-center text-[10px] font-medium uppercase tracking-wider text-theme-fg-muted">
                        {label}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Credit balance row */}
                <div className="border-t border-theme-border px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="material-icons-outlined text-[18px] text-green-600">account_balance_wallet</span>
                      <div>
                        <p className="text-[11px] font-medium uppercase tracking-wider text-theme-fg-muted">Credits</p>
                        <p className={`text-[18px] font-bold leading-none ${(usage.credit_balance ?? 0) < 2 ? "text-amber-500" : "text-theme-fg"}`}>
                          ${(usage.credit_balance ?? 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setIsUsageOpen(false); navigate("/billing"); }}
                      className="rounded-lg border border-theme-primary bg-theme-primary/10 px-3 py-1.5 text-[12px] font-semibold text-theme-primary transition-colors hover:bg-theme-primary hover:text-white"
                    >
                      Add Credits
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {embedded && agentId && customerId && (
          <button
            type="button"
            onClick={onEditAgent}
            className="flex items-center gap-1.5 rounded-lg border border-theme-border bg-theme-surface px-2.5 py-1.5 text-sm font-medium leading-snug text-theme-fg shadow-sm transition-colors hover:bg-theme-background"
          >
            <span className="material-icons-outlined text-lg text-theme-fg-muted">edit</span>
            Edit Agent
          </button>
        )}
        {onBack && !embedded && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onBack();
            }}
            className="z-10 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium leading-snug text-theme-primary transition-colors hover:bg-theme-primary/10"
            aria-label="Back to agent list"
            type="button"
          >
            <span className="material-icons-outlined text-lg">arrow_back</span>
            Back
          </button>
        )}
      </div>
    </header>
  );
};
